import React from "react";
import { Link } from "react-router-dom";
import { STORY_BLOCKS } from "../mock";
import { ChevronRight } from "lucide-react";

export default function About() {
  return (
    <main>
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-14 md:pt-20 text-center">
        <span className="text-[11px] tracking-[0.32em] uppercase text-[hsl(var(--kq-accent-2))]">— Our story</span>
        <h1 className="font-display text-5xl md:text-7xl leading-[1.02] mt-4">Made by hand, for the way you live.</h1>
        <div className="mt-5 flex items-center justify-center"><span className="kq-thin-rule" /></div>
        <div className="font-italic text-base md:text-lg text-[hsl(var(--kq-ink-soft))] leading-relaxed mt-7 max-w-2xl mx-auto space-y-5 text-left sm:text-center">
          <p>Kaftan Queens was born out of a love of travel, colour and casual fashion.</p>
          <p>Timeless, easy-to-wear kaftans, dresses, scarves &amp; men&apos;s shirts that bring joy. Each piece is sourced by me personally and ethically made in India — and all my scarves are hand stitched.</p>
          <p>From sun-drenched mornings to layered winter escapes, Kaftan Queens is made to be lived in and enjoyed — anywhere.</p>
          <p>Thank you for being part of our journey so far.</p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 md:px-10 mt-14 md:mt-20">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Photo — full image always visible, never cropped */}
          <div className="md:col-span-7 lg:col-span-6 bg-[hsl(var(--kq-bg-2))] flex items-center justify-center">
            <picture>
              <source media="(max-width: 767px)" srcSet="/about/our-story-mobile.jpg" />
              <img
                src="/about/our-story-desktop.jpg"
                alt="Our story — Kaftan Queens"
                loading="lazy"
                decoding="async"
                className="block w-auto max-w-full max-h-[80vh] md:max-h-[720px] object-contain"
              />
            </picture>
          </div>
          {/* Side caption */}
          <aside className="md:col-span-5 lg:col-span-6">
            <span className="text-[11px] tracking-[0.32em] uppercase text-[hsl(var(--kq-accent-2))]">— Founder note</span>
            <h2 className="font-display text-3xl md:text-4xl leading-[1.05] mt-3">A wardrobe made with love.</h2>
            <div className="mt-4"><span className="kq-thin-rule" /></div>
            <p className="font-italic text-base md:text-lg text-[hsl(var(--kq-ink-soft))] leading-relaxed mt-5">
              Hand-picked in India, finished by artisan hand, and worn from sunrise on the beach to candlelit evenings at home.
            </p>
          </aside>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-20 md:mt-28 grid md:grid-cols-2 gap-10 md:gap-20">
        <div>
          <h2 className="font-display text-3xl md:text-5xl leading-[1.05]">We pride ourselves on individuality.</h2>
          <div className="mt-4"><span className="kq-thin-rule" /></div>
        </div>
        <div className="font-italic text-lg text-[hsl(var(--kq-ink-soft))] space-y-4 leading-relaxed">
          <p>Every scarf is woven on its own loom. Every kaftan is hand block-printed. Nothing is mass-produced, ensuring originality in every piece you wear.</p>
          <p>We work directly with artisan families across Northern India — supporting heritage craft and ensuring fair, dignified livelihoods.</p>
          <p>The result is clothing with a soul. Pieces that age beautifully and tell a story of the hands that made them.</p>
        </div>
      </section>

      <section className="mt-24 md:mt-32 bg-[hsl(var(--kq-bg-2))] py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <span className="text-[11px] tracking-[0.32em] uppercase text-[hsl(var(--kq-accent-2))]">— Our values</span>
            <h2 className="font-display text-4xl md:text-5xl mt-3">Slow, honest, lasting</h2>
            <div className="mt-4 flex items-center justify-center"><span className="kq-thin-rule" /></div>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {STORY_BLOCKS.map((s, i) => (
              <article key={s.title} className="text-center">
                <div className="aspect-[4/5] kq-img-zoom mb-5">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                </div>
                <span className="font-italic text-sm text-[hsl(var(--kq-accent-2))]">0{i + 1}</span>
                <h3 className="font-display text-2xl md:text-3xl mt-2">{s.title}</h3>
                <p className="font-italic text-base md:text-lg text-[hsl(var(--kq-ink-soft))] mt-3 leading-relaxed max-w-sm mx-auto">{s.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 md:px-10 mt-24 md:mt-32 text-center pb-8">
        <h2 className="font-display text-4xl md:text-6xl leading-[1.04] max-w-3xl mx-auto">Wear a story — not a logo.</h2>
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 mt-8 bg-[hsl(var(--kq-ink))] text-[hsl(var(--kq-bg))] px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[hsl(var(--kq-accent-2))] transition-colors"
        >
          Explore the Collection <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </section>
    </main>
  );
}
